import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { Testimonial } from '../Testimonial/Testimonial';

export interface TestimonialItem {
  content: string;
  rating: number;
}

interface TestimonialCarouselProps {
  speed?: number;
  fadeOutColor?: string;
}

const ANIMATION_CONFIG = {
  SMOOTH_TAU: 0.25,
  MIN_COPIES: 2,
  COPY_HEADROOM: 2,
} as const;

export const TESTIMONIALS: TestimonialItem[] = [
  {
    content:
      'This is our first time using CARCAREERS.COM and they were great. They answered all my questions to understand what we need for our business. They were professional, quick and pleasant to work with. Great experience.',
    rating: 5,
  },
  {
    content:
      'CARCAREERS.COM was absolutely terrific on getting our FORD DEALERSHIP some great salespeople. We are also partnering with them again on getting more Technicians!! I would highly recommend them to get you qualified staff for your dealership.',
    rating: 5,
  },
  {
    content:
      'I own A Dealership in Greer SC. We have been using this recruiting service for about a year. CARCAREERS.COM has helped us add several key people to our growing dealership. The communication is seamless, the service is affordable and every part of the process is done professionally. They know the automotive industry so they already are a step ahead of other recruiters...',
    rating: 5,
  },
  {
    content:
      'CARCAREERS.COM took the time to understand the staffing needs at Countryside Chevrolet. They worked closely with our service manager to find qualified candidates and solve any issues he had. The company is professional and diligent. Thank you CARCAREERS.COM.',
    rating: 5,
  },
  {
    content:
      "I have been using CARCAREERS.COM for over three years and cannot stress how beneficial their services have been to our organization. The amount of time they save us and the quality of candidates they have provided can't be quantified. They are extremely professional, timely and thorough, highly recommend this company!!",
    rating: 5,
  },
  {
    content:
      'We have used CARCAREERS.COM twice now. Always get solid results and a great response rate from their staffing efforts. We would definitely recommend them to anyone that has a need to hire employees.',
    rating: 5,
  },
  {
    content:
      'CARCAREERS.COM has been great to work with and we have hired quite a few individuals from their services.',
    rating: 5,
  },
  {
    content:
      'CARCAREERS.COM has been easy to work with. They answered on my first call after being referred to CARCAREERS.COM by another dealer who used them with success and we have already had a qualified applicant on our first day live with the program! Highly recommend.',
    rating: 5,
  },
  {
    content:
      'So happy to have CARCAREERS.COM at our finger tips to help us with our staffing needs. We recently lost an employee due to retirement. I immediately reached out to CARCAREERS.COM and within minutes they were contacting me for a description of our needs. They made the process quick and easy which is just what we need as business owners in a fast paced industry. Thank you CARCAREERS.COM!',
    rating: 5,
  },
  {
    content:
      'Absolutely thrilled with my experience so far working with CARCAREERS.COM. From the moment we connected, their professionalism and dedication were evident. They took the time to understand my needs to filling a key role within our company. Thank you, CARCAREERS.COM, for making this journey so smooth and successful.',
    rating: 5,
  },
];

export const TestimonialCarousel = ({ speed = 30, fadeOutColor }: TestimonialCarouselProps) => {
  const testimonials = TESTIMONIALS;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const seqRef = useRef<HTMLDivElement>(null);

  const [seqWidth, setSeqWidth] = useState<number>(0);
  const [copyCount, setCopyCount] = useState<number>(ANIMATION_CONFIG.MIN_COPIES);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const rafRef = useRef<number | null>(null);
  const lastTimestampRef = useRef<number | null>(null);
  const offsetRef = useRef(0);
  const velocityRef = useRef(0);

  // Drag state
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartOffsetRef = useRef(0);

  // Calculate card width based on viewport
  const cardWidth = useMemo(() => {
    if (isMobile) return 320; // Full width minus padding
    if (isTablet) return 350; // 2 cards on tablet
    return 400; // 3 cards on desktop
  }, [isMobile, isTablet]);

  const gap = 24;

  const updateDimensions = useCallback(() => {
    const containerWidth = containerRef.current?.clientWidth ?? 0;
    const sequenceRect = seqRef.current?.getBoundingClientRect?.();
    const sequenceWidth = sequenceRect?.width ?? 0;

    if (sequenceWidth > 0) {
      setSeqWidth(Math.ceil(sequenceWidth));
      const copiesNeeded = Math.ceil(containerWidth / sequenceWidth) + ANIMATION_CONFIG.COPY_HEADROOM;
      setCopyCount(Math.max(ANIMATION_CONFIG.MIN_COPIES, copiesNeeded));
    }
  }, []);

  // Handle resize
  useEffect(() => {
    const handleResize = () => updateDimensions();
    window.addEventListener('resize', handleResize);
    updateDimensions();
    return () => window.removeEventListener('resize', handleResize);
  }, [updateDimensions, testimonials, cardWidth]);

  // Animation loop
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const animate = (timestamp: number) => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = timestamp;
      }

      const deltaTime = Math.max(0, timestamp - lastTimestampRef.current) / 1000;
      lastTimestampRef.current = timestamp;

      const targetSpeed = isHovered ? 0 : speed;
      const easingFactor = 1 - Math.exp(-deltaTime / ANIMATION_CONFIG.SMOOTH_TAU);
      velocityRef.current += (targetSpeed - velocityRef.current) * easingFactor;

      if (seqWidth > 0) {
        let nextOffset = offsetRef.current + velocityRef.current * deltaTime;
        nextOffset = ((nextOffset % seqWidth) + seqWidth) % seqWidth;
        offsetRef.current = nextOffset;

        track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTimestampRef.current = null;
    };
  }, [speed, seqWidth, isHovered]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  // Drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragStartOffsetRef.current = offsetRef.current;
    if (trackRef.current) {
      trackRef.current.style.cursor = 'grabbing';
    }
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDraggingRef.current || !seqWidth) return;

      const deltaX = dragStartXRef.current - e.clientX;
      let newOffset = dragStartOffsetRef.current + deltaX;
      newOffset = ((newOffset % seqWidth) + seqWidth) % seqWidth;
      offsetRef.current = newOffset;

      if (trackRef.current) {
        trackRef.current.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
      }
    },
    [seqWidth],
  );

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    if (trackRef.current) {
      trackRef.current.style.cursor = 'grab';
    }
  }, []);

  // Add global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        if (trackRef.current) {
          trackRef.current.style.cursor = 'grab';
        }
      }
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  const testimonialLists = useMemo(
    () =>
      Array.from({ length: copyCount }, (_, copyIndex) => (
        <Box
          key={`copy-${copyIndex}`}
          ref={copyIndex === 0 ? seqRef : undefined}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: `${gap}px`,
          }}
        >
          {testimonials.map((testimonial, itemIndex) => (
            <Box
              key={`${copyIndex}-${itemIndex}`}
              sx={{
                flex: '0 0 auto',
                width: `${cardWidth}px`,
              }}
            >
              <Testimonial content={testimonial.content} rating={testimonial.rating} />
            </Box>
          ))}
        </Box>
      )),
    [copyCount, testimonials, cardWidth, gap],
  );

  const effectiveFadeColor = fadeOutColor || (theme.palette.mode === 'dark' ? '#0b0b0b' : '#ffffff');

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: '5px',
          pointerEvents: 'none',
          zIndex: 10,
        },
        '&::before': {
          left: 0,
          background: `linear-gradient(to right, ${effectiveFadeColor} 0%, rgba(0, 0, 0, 0) 100%)`,
        },
        '&::after': {
          right: 0,
          background: `linear-gradient(to left, ${effectiveFadeColor} 0%, rgba(0, 0, 0, 0) 100%)`,
        },
      }}
      role="region"
      aria-label="Customer testimonials"
    >
      <Box
        ref={trackRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        sx={{
          display: 'flex',
          gap: `${gap}px`,
          width: 'max-content',
          willChange: 'transform',
          userSelect: 'none',
          position: 'relative',
          zIndex: 0,
          cursor: 'grab',
          '@media (prefers-reduced-motion: reduce)': {
            transform: 'translate3d(0, 0, 0) !important',
          },
        }}
      >
        {testimonialLists}
      </Box>
    </Box>
  );
};
